import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import FloatingCTA from "./components/FloatingCTA";
import PageSEO from "./components/PageSEO";
import HomePage from "./pages/HomePage";
import VirtualStagingPage from "./pages/VirtualStagingPage";
import ThreeDRendersPage from "./pages/ThreeDRendersPage";
import OrderPage from "./pages/OrderPage";
import CheckoutSuccessPage from "./pages/CheckoutSuccessPage";
import AdminPage from "./pages/AdminPage";
import ServicesRedirectPage from "./pages/ServicesRedirectPage";
import BlogListPage from "./pages/BlogListPage";
import BlogPostPage from "./pages/BlogPostPage";
import InvoicePage from "./pages/InvoicePage";
import LandingEstateAgentsPage from "./pages/LandingEstateAgentsPage";
import LandingSellEmptyPropertyPage from "./pages/LandingSellEmptyPropertyPage";
import LandingStagedSellFasterPage from "./pages/LandingStagedSellFasterPage";
import LandingVsPhysicalStagingPage from "./pages/LandingVsPhysicalStagingPage";
import LandingNewBuildPage from "./pages/LandingNewBuildPage";
import ROICalculatorPage from "./pages/ROICalculatorPage";

function SEOWrapper({ children }) {
  const location = useLocation();

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <>
      <PageSEO path={location.pathname} />
      {children}
    </>
  );
}

// Structured Data for Google Rich Results
function StructuredData() {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const localBusiness = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "The Secret Seller",
    "description": "UK's premier virtual staging and 3D render service for estate agents, developers and landlords.",
    "url": origin,
    "email": "rachel@thesecretseller.co.uk",
    "areaServed": { "@type": "Country", "name": "United Kingdom" },
    "priceRange": "£55-£65",
    "image": "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/mfir6td4_Manchester%20Road%20kitchen%201.jpg",
    "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5", "reviewCount": "3" },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Property Marketing Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": { "@type": "Service", "name": "Virtual Staging", "description": "Turn empty rooms into sale-ready spaces in 48 hours" },
          "price": "55.00",
          "priceCurrency": "GBP",
          "priceSpecification": { "@type": "UnitPriceSpecification", "price": "55.00", "priceCurrency": "GBP", "unitText": "per image (10+ images)" }
        },
        {
          "@type": "Offer",
          "itemOffered": { "@type": "Service", "name": "3D Property Renders", "description": "Photorealistic 3D renders for property developers" }
        }
      ]
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is virtual staging and how does it work?",
        "acceptedAnswer": { "@type": "Answer", "text": "Virtual staging is the process of digitally furnishing and decorating photos of empty rooms. You upload your property photos, our professional designers add furniture, decor and styling, and you receive photorealistic staged images within 48 hours. It's the fastest, most affordable way to make your listings stand out on Rightmove, Zoopla and other property portals." }
      },
      {
        "@type": "Question",
        "name": "How much does virtual staging cost in the UK?",
        "acceptedAnswer": { "@type": "Answer", "text": "Our virtual staging starts at £65 per image, with a bulk discount of £55 per image when you order 10 or more. This is a fraction of the cost of physical staging, which typically costs £2,000+ per property. There's no subscription — you only pay for what you need." }
      },
      {
        "@type": "Question",
        "name": "How long does virtual staging take?",
        "acceptedAnswer": { "@type": "Answer", "text": "We deliver all virtually staged images within 48 hours of receiving your photos." }
      },
      {
        "@type": "Question",
        "name": "What types of rooms can you virtually stage?",
        "acceptedAnswer": { "@type": "Answer", "text": "We can virtually stage any room type including living rooms, bedrooms, kitchens, dining rooms, bathrooms, home offices, hallways, conservatories and lofts. We offer a range of interior styles including modern, luxury, neutral, family, Scandinavian, contemporary, classic, industrial and minimalist." }
      },
      {
        "@type": "Question",
        "name": "Do you offer 3D property renders for developers?",
        "acceptedAnswer": { "@type": "Answer", "text": "Yes, we provide photorealistic 3D renders for property developers, including both exterior and interior architectural visualisations. These are ideal for off-plan marketing, planning applications and sales brochures." }
      },
      {
        "@type": "Question",
        "name": "Which estate agents use The Secret Seller?",
        "acceptedAnswer": { "@type": "Answer", "text": "We work with leading UK estate agents including Savills, Jones Homes and hundreds of independent agents across England, Scotland and Wales." }
      }
    ]
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify((localBusiness)) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify((faqSchema)) }}
      />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <div className="min-h-screen bg-white">
          <StructuredData />
          <Toaster position="top-right" richColors />
          <Navbar />
          <SEOWrapper>
            <Routes>
              {/* Primary pages */}
              <Route path="/" element={<HomePage />} />
              <Route path="/virtual-staging-uk" element={<VirtualStagingPage />} />
              <Route path="/3d-property-rendering-uk" element={<ThreeDRendersPage />} />
              <Route path="/360-virtual-tours-uk" element={<Navigate to="/virtual-staging-uk" replace />} />
              <Route path="/order" element={<OrderPage />} />
              <Route path="/services" element={<ServicesRedirectPage />} />
              <Route path="/blog" element={<BlogListPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
              <Route path="/invoice/:invoiceId" element={<InvoicePage />} />
              <Route path="/virtual-staging-estate-agents" element={<LandingEstateAgentsPage />} />
              <Route path="/how-to-sell-empty-property" element={<LandingSellEmptyPropertyPage />} />
              <Route path="/do-staged-properties-sell-faster" element={<LandingStagedSellFasterPage />} />
              <Route path="/virtual-staging-vs-physical-staging" element={<LandingVsPhysicalStagingPage />} />
              <Route path="/property-marketing-new-builds" element={<LandingNewBuildPage />} />
              <Route path="/virtual-staging-roi-calculator" element={<ROICalculatorPage />} />
              <Route path="/admin" element={<AdminPage />} />

              {/* 301 Redirects: Old internal URLs → new SEO URLs */}
              <Route path="/virtual-staging" element={<Navigate to="/virtual-staging-uk" replace />} />
              <Route path="/3d-renders" element={<Navigate to="/3d-property-rendering-uk" replace />} />
              <Route path="/360-tours" element={<Navigate to="/virtual-staging-uk" replace />} />

              {/* 301 Redirects: Old Shopify URLs → closest content match */}
              <Route path="/products/virtual-staging" element={<Navigate to="/virtual-staging-uk" replace />} />
              <Route path="/products/interior-property-renders" element={<Navigate to="/3d-property-rendering-uk" replace />} />
              <Route path="/products/exterior-property-renders-295" element={<Navigate to="/3d-property-rendering-uk" replace />} />
              <Route path="/products/bespoke-mood-board" element={<Navigate to="/services" replace />} />
              <Route path="/products/residential-property-report" element={<Navigate to="/services" replace />} />
              <Route path="/products/*" element={<Navigate to="/services" replace />} />
              <Route path="/collections/all" element={<Navigate to="/services" replace />} />
              <Route path="/collections/*" element={<Navigate to="/services" replace />} />
              <Route path="/pages/contact-us" element={<Navigate to="/services" replace />} />
              <Route path="/pages/*" element={<Navigate to="/services" replace />} />
              <Route path="/blogs/*" element={<Navigate to="/" replace />} />

              {/* Catch-all: unmatched URLs → services */}
              <Route path="*" element={<ServicesRedirectPage />} />
            </Routes>
          </SEOWrapper>
          <Footer />
          <FloatingCTA />
        </div>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
